import { useState } from 'react'
import { Link, useNavigate, useOutletContext } from 'react-router-dom'

import { createGroupConfig } from '../../../api/groupConfigs.js'
import { getSegmentation } from '../../../api/segmentations.js'
import { actions } from '../../../local/remoteConfigStore.js'
import { useRemoteConfigMemo } from '../../../local/useRemoteConfigStore.js'

const ELEMENT_TYPES = [
  { value: 'advertising', label: 'Advertising' },
  { value: 'inapp', label: 'In-App' },
  { value: 'balance', label: 'Balance' },
]

function validate(values) {
  const errors = {}
  if (!String(values.name || '').trim()) errors.name = 'Name is required'
  if (!String(values.startBundleVersion || '').trim()) errors.startBundleVersion = 'Start bundle version is required'
  if (!String(values.defaultElementName || '').trim()) errors.defaultElementName = 'Default element name is required'
  return errors
}

export default function GroupConfigCreatePage() {
  const navigate = useNavigate()
  const { applicationId } = useOutletContext()
  const storeActions = useRemoteConfigMemo(() => actions(), [])

  const [values, setValues] = useState({
    name: '',
    description: '',
    startBundleVersion: '',
    defaultElementName: '',
    defaultElementType: 'advertising',
    defaultElementDescription: '',
    defaultElementJsonConfig: '',
  })
  const [errors, setErrors] = useState({})
  const [submitError, setSubmitError] = useState(null)
  const [isSubmitting, setIsSubmitting] = useState(false)

  function onChange(field) {
    return (event) => {
      setValues((prev) => ({ ...prev, [field]: event.target.value }))
    }
  }

  async function handleSubmit(event) {
    event.preventDefault()
    if (isSubmitting) return
    setSubmitError(null)

    const nextErrors = validate(values)
    setErrors(nextErrors)
    if (Object.keys(nextErrors).length > 0) return

    setIsSubmitting(true)
    try {
      const created = await createGroupConfig(applicationId, values)
      let defaultSegmentation = null
      if (created.defaultSegmentationId) {
        try {
          defaultSegmentation = await getSegmentation(applicationId, created.defaultSegmentationId)
        } catch {
          defaultSegmentation = null
        }
      }
      storeActions.hydrateGroupConfigDraft(applicationId, {
        groupConfigId: created.id,
        name: created.name,
        description: created.description,
        startBundleVersion: created.startBundleVersion,
        draftRemoteConfigId: created.draftRemoteConfigId,
        defaultSegmentationId: created.defaultSegmentationId,
        defaultGroupSegmentId: created.defaultGroupSegmentId,
        defaultSegmentation,
        createdAt: created.createdAt,
        updatedAt: created.updatedAt,
      })
      navigate(`/applications/${applicationId}/configs/${created.id}/builder`)
    } catch (error) {
      setSubmitError(error)
    } finally {
      setIsSubmitting(false)
    }
  }

  return (
    <div className="page">
      <div className="pageHeader">
        <h1 className="pageTitle">Create Group Config</h1>
        <Link to={`/applications/${applicationId}/configs`} className="button buttonGhost">
          Back
        </Link>
      </div>

      <section className="card formCard">
        <div className="cardTitle">Group config</div>
        <form className="formGrid" onSubmit={handleSubmit}>
          <label className="formField">
            <span className="formLabel">Name</span>
            <input
              className="textInput"
              value={values.name}
              onChange={onChange('name')}
              placeholder="e.g. economy_v1"
            />
            {errors.name ? <span className="muted">{errors.name}</span> : null}
          </label>

          <label className="formField">
            <span className="formLabel">Description</span>
            <input
              className="textInput"
              value={values.description}
              onChange={onChange('description')}
              placeholder="Optional"
            />
          </label>

          <label className="formField">
            <span className="formLabel">Start bundle version</span>
            <input
              className="textInput"
              value={values.startBundleVersion}
              onChange={onChange('startBundleVersion')}
              placeholder="e.g. 1.2.3"
            />
            {errors.startBundleVersion ? <span className="muted">{errors.startBundleVersion}</span> : null}
          </label>

          <div className="cardTitle" style={{ marginTop: 16 }}>Default element</div>

          <label className="formField">
            <span className="formLabel">Element name</span>
            <input
              className="textInput"
              value={values.defaultElementName}
              onChange={onChange('defaultElementName')}
              placeholder="e.g. economy_element_v1"
            />
            {errors.defaultElementName ? <span className="muted">{errors.defaultElementName}</span> : null}
          </label>

          <label className="formField">
            <span className="formLabel">Element type</span>
            <select
              className="textInput"
              value={values.defaultElementType}
              onChange={onChange('defaultElementType')}
            >
              {ELEMENT_TYPES.map((t) => (
                <option key={t.value} value={t.value}>
                  {t.label}
                </option>
              ))}
            </select>
          </label>

          <label className="formField">
            <span className="formLabel">Element description</span>
            <input
              className="textInput"
              value={values.defaultElementDescription}
              onChange={onChange('defaultElementDescription')}
              placeholder="Optional"
            />
          </label>

          <label className="formField">
            <span className="formLabel">JSON config</span>
            <textarea
              className="textInput textAreaMonoLg"
              value={values.defaultElementJsonConfig}
              onChange={onChange('defaultElementJsonConfig')}
              placeholder='{"key": "value"}'
            />
          </label>

          <div className="formActions">
            <button type="submit" className="button buttonPrimary" disabled={isSubmitting}>
              {isSubmitting ? 'Creating…' : 'Create'}
            </button>
          </div>
        </form>

        {submitError ? (
          <div className="formError">{String(submitError?.message || submitError)}</div>
        ) : null}
      </section>
    </div>
  )
}

